import { useContext, useState, useEffect, useMemo } from "react";
import { AuthContext } from "../../context/AuthContext";
import Preloader from "../../components/Preloader";
import { QueryBuilder, formatQuery } from 'react-querybuilder';
import { ReportesService } from "../../services/ReportesService";

import 'react-querybuilder/dist/query-builder.css';
import './RegisterReporteConfig.css';
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Permiso from "../../router/Permiso";

const RegisterReportConfig = () => {
    const { token } = useContext(AuthContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [selectedTable, setSelectedTable] = useState("");
    const [relatedTables, setRelatedTables] = useState([]);
    const [availableRelations, setAvailableRelations] = useState([]);
    const [fields, setFields] = useState([]);
    const [query, setQuery] = useState({ combinator: 'and', rules: [] });
    const [reportType, setReportType] = useState("listado");
    const [reportName, setReportName] = useState("");
    const [reportDescription, setReportDescription] = useState("");
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [errors, setErrors] = useState({});
    const [limit, setLimit] = useState("");
    const [groupByColumns, setGroupByColumns] = useState([]);
    const [orderByColumn, setOrderByColumn] = useState("");
    const [orderDirection, setOrderDirection] = useState("ASC");
    const [temporalGranularity, setTemporalGranularity] = useState('dia');

    const hayAlgunaFechaSeleccionada = useMemo(() => {
        return selectedColumns.some(colObj => {
            const colId = colObj.id;

            const field = fields.find(f => f.name === colId);

            return (
                field?.type === 'date' ||
                field?.type === 'datetime' ||
                colId.toLowerCase().includes('fecha')
            );
        });
    }, [selectedColumns, fields]);

    const esColumnaFecha = (colName, fields) => {
        const field = fields.find(f => f.name === colName);

        if (field && (field.type === 'date' || field.type === 'datetime' || field.type === 'timestamp')) {
            return true;
        }

        const keywords = ['fecha', 'date', 'created_at', 'updated_at', 'pagado_el'];
        return keywords.some(key => colName.toLowerCase().includes(key));
    };

    useEffect(() => {
        if (!selectedTable) return;

        const loadBaseSchema = async () => {
            setLoading(true);
            try {
                const data = await ReportesService.getSchema({ token, table: selectedTable });

                const baseFields = (data.fields || []).map(f => ({
                    ...f,
                    name: `${selectedTable}.${f.name}`,
                    label: `(Base) ${f.label}`,
                    relId: 'base',
                    isBase: true
                }));

                setFields(baseFields);
                setAvailableRelations(data.relations || []);
            } catch (error) {
                console.error("Error cargando tabla base:", error);
                toast.error("Error al cargar el esquema de la tabla");
            } finally {
                setLoading(false);
            }
        };

        loadBaseSchema();
    }, [selectedTable, token]);

    useEffect(() => {
        if (!selectedTable || relatedTables.length === 0) return;

        const updateRecursiveRelations = async () => {
            try {
                const baseData = await ReportesService.getSchema({ token, table: selectedTable });
                let allRelations = [...(baseData?.relations || [])];

                for (const rel of relatedTables) {
                    const relData = await ReportesService.getSchema({ token, table: rel.table, parentAlias: rel.alias });
                    if (relData?.relations) {
                        const mappedSubRelations = relData.relations.map(sub => ({
                            ...sub,
                            parentAlias: rel.alias 
                        }));
                        allRelations = [...allRelations, ...mappedSubRelations];
                    }
                }

                const uniqueRelations = Array.from(new Map(allRelations.map(item => [item.id, item])).values());
                setAvailableRelations(uniqueRelations);
            } catch (error) {
                console.error("Error en recursión:", error);
            }
        };

        updateRecursiveRelations();
    }, [relatedTables]);

    const toggleRelation = async (rel, parentAlias = null) => {
        const isAlreadyRelated = relatedTables.some(r => r.id === rel.id);

        if (isAlreadyRelated) {
            setFields(prev => prev.filter(f => f.relId !== rel.id));
            setRelatedTables(prev => prev.filter(r => r.id !== rel.id));
            setSelectedColumns(prev => prev.filter(col => !col.id.startsWith(rel.alias + '.')));
            setGroupByColumns(prev => prev.filter(col => !col.startsWith(rel.alias + '.')));
        } else {
            setLoading(true);
            try {
                const actualParent = parentAlias || selectedTable;

                const data = await ReportesService.getSchema({
                    token,
                    table: rel.table,
                    parentAlias: actualParent
                });

                const newRelation = {
                    ...rel,
                    joinCondition: data.relations.find(r => r.id === rel.id)?.joinCondition || rel.joinCondition
                };

                const formattedNewFields = (data.fields || []).map(f => ({
                    ...f,
                    name: `${rel.alias}.${f.name}`,
                    label: `${rel.alias.toUpperCase()} - ${f.label}`,
                    relId: rel.id
                }));

                setFields(prev => [...prev, ...formattedNewFields]);
                setRelatedTables(prev => [...prev, newRelation]);

            } catch (error) {
                toast.error("No se pudo cargar la relación");
                console.log(error);
            } finally {
                setLoading(false);
            }
        }
    };

    const buildSQL = () => {
        if (!selectedTable) return "";

        let columnSelection = "";
        let orderByClause = "";
        let finalGroupBy = "";

        if (reportType === "listado") {
            if (selectedColumns.length > 0) {
                columnSelection = selectedColumns.map(col => {
                    const aliasFinal = col.alias || col.id.replace('.', '_');
                    const isDateField = esColumnaFecha(col.id, fields);

                    if (isDateField && temporalGranularity && !col.id.toLowerCase().includes('hora')) {
                        let formato = '%Y-%m';
                        if (temporalGranularity === 'dia') formato = '%Y-%m-%d';
                        if (temporalGranularity === 'anio') formato = '%Y';

                        return `DATE_FORMAT(${col.id}, '${formato}') AS "${aliasFinal}"`;
                    }

                    return `${col.id} AS "${aliasFinal}"`;
                }).join(', ');
            } else {
                columnSelection = "*";
            }

            finalGroupBy = groupByColumns.length > 0
                ? ` GROUP BY ${groupByColumns.join(', ')}`
                : '';
        } else {

            const metricas = selectedColumns.filter(colObj => esColumnaNumerica(colObj.id, fields));
            const dimensiones = selectedColumns.filter(colObj => !esColumnaNumerica(colObj.id, fields));

            if (dimensiones.length > 0 && metricas.length > 0) {
                const dimensionesProcesadas = dimensiones.map(dimObj => {
                    const d = dimObj.id;
                    if (esColumnaFecha(d, fields)) {
                        const gran = temporalGranularity || 'mes';
                        let formato = '%Y-%m';
                        if (gran === 'dia') formato = '%Y-%m-%d';
                        if (gran === 'anio') formato = '%Y';
                        return `DATE_FORMAT(${d}, '${formato}')`;
                    }
                    return d;
                });

                let dimensionSQL = dimensionesProcesadas.length > 1
                    ? `CONCAT(${dimensionesProcesadas.map(d => `COALESCE(${d}, '')`).join(", ' ', ")})`
                    : dimensionesProcesadas[0];

                const metricaRaw = metricas[0].id;
                const agregacion = metricaRaw.toLowerCase().endsWith('.id')
                    ? `COUNT(${metricaRaw})`
                    : `SUM(${metricaRaw})`;

                columnSelection = `${dimensionSQL} AS etiqueta, ${agregacion} AS valor`;
                finalGroupBy = ` GROUP BY ${dimensionSQL}`;
                orderByClause = ` ORDER BY valor DESC`;
            }
        }

        if (orderByColumn) {
            orderByClause = ` ORDER BY ${orderByColumn} ${orderDirection}`;
        }


        const joins = relatedTables.map(rel => ` INNER JOIN ${rel.table} AS ${rel.alias} ON ${rel.joinCondition}`).join('');
        const whereClause = formatQuery(query, 'sql') || '1=1';
        const limitClause = limit && limit > 0 ? ` LIMIT ${limit}` : '';

        return `SELECT ${columnSelection} FROM ${selectedTable} AS ${selectedTable}${joins} WHERE ${whereClause}${finalGroupBy}${orderByClause}${limitClause}`;
    };

    const guardarConfiguracion = async () => {
        let newErrors = {};
        if (!reportName.trim()) newErrors.reportName = "El nombre es obligatorio";
        if (!reportDescription.trim()) newErrors.reportDescription = "La descripción es obligatoria";
        if (!selectedTable) newErrors.selectedTable = "Debes seleccionar una tabla de origen";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const errorSQL = validarConfiguracionSQL();
        if (errorSQL) {
            toast.error(errorSQL);
            return;
        }

        const sqlFinal = buildSQL();

        const dataPayload = {
            nombre_reporte: reportName,
            descripcion_reporte: reportDescription,
            tipo_reporte: reportType,
            tabla_base: selectedTable,
            tablas_relacionadas: relatedTables,
            columnas_seleccionadas: selectedColumns,
            columnas_grupo: reportType === "listado" ? groupByColumns : [selectedColumns.find(col => !esColumnaNumerica(col, fields))],
            query_json: query,
            limit: limit,
            orderByColumn: orderByColumn,
            orderDirection: orderDirection,
            sql_generado: sqlFinal
        };

        try {
            setLoading(true);
            await ReportesService.saveConfig({ token, data: dataPayload });
            toast.success("¡Reporte guardado con éxito!");
            navigate('/admin/reporteconfig');
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const esColumnaNumerica = (colName, fieldsList) => {
        const info = fieldsList.find(f => f.name === colName);
        const tipo = (info?.dataType || info?.type || "").toLowerCase();
        return tipo.includes('int') || tipo.includes('decimal') ||
            tipo.includes('float') || tipo.includes('double') ||
            tipo.includes('number');
    };

    const handleClear = () => {
        setQuery({ combinator: 'and', rules: [] });
    };

    const VALIDACIONES_REPORTE = {
        listado: { minColumnas: 1, mensaje: "Un listado debe tener al menos una columna seleccionada." },
        barra: { minColumnas: 2, checkNumeric: true, mensaje: "El gráfico de barras requiere una etiqueta y un valor numérico." },
        linea: { minColumnas: 2, checkNumeric: true, mensaje: "El gráfico de líneas requiere una serie o categoría y un valor numérico." },
        pie: { minColumnas: 2, checkNumeric: true, mensaje: "El gráfico de pastel requiere una categoría y un valor numérico." },
        dona: { minColumnas: 2, checkNumeric: true, mensaje: "El gráfico de dona requiere una categoría y un valor numérico." },
        area: { minColumnas: 2, checkNumeric: true, mensaje: "El gráfico de área requiere una serie temporal/categoría y un valor numérico." },
        radar: { minColumnas: 2, checkNumeric: true, mensaje: "El gráfico de radar requiere al menos una categoría y una métrica numérica." }
    };

    const validarConfiguracionSQL = () => {
        const reglas = VALIDACIONES_REPORTE[reportType];
        const columnasSeleccionadasInfo = fields.filter(f =>
            selectedColumns.some(colObj => colObj.id === f.name)
        );

        if (columnasSeleccionadasInfo.length < reglas.minColumnas) {
            return reglas.mensaje;
        }

        if (reportType !== 'listado') {
            const tieneNumerico = selectedColumns.some(colObj =>
                esColumnaNumerica(colObj.id, fields)
            );

            if (!tieneNumerico) {
                return `El reporte de tipo "${reportType}" requiere al menos una columna numérica (métrica).`;
            }

            const tieneDimension = selectedColumns.some(colObj =>
                !esColumnaNumerica(colObj.id, fields)
            );

            if (!tieneDimension) {
                return `El reporte de tipo "${reportType}" requiere una columna de texto o fecha para las etiquetas.`;
            }
        }
        return null;
    };

    return (
        <div className="reporte-admin-container">
            {loading && <Preloader />}

            <div className="reporte-admin-header">
                <h1 className="reporte-admin-title">Configuración de Reportes Dinámicos</h1>
            </div>

            <div className="reporte-card">
                <div className="reporte-card-body">
                    <div className="form-group-full">
                        <label className="form-label">Nombre del Reporte</label>
                        <input
                            type="text"
                            className="form-control-input-large"
                            placeholder="Escribe el nombre del reporte aquí..."
                            value={reportName}
                            onChange={(e) => setReportName(e.target.value)}
                        />
                        {errors.reportName && <span className="error-text">{errors.reportName}</span>}
                    </div>
                    <div className="form-group-full" style={{ marginTop: '1rem' }}>
                        <label className="form-label">Descripción del Reporte</label>
                        <textarea
                            className="form-control-input-large"
                            style={{ minHeight: '80px', resize: 'vertical' }}
                            placeholder="Describe brevemente el propósito de este reporte..."
                            value={reportDescription}
                            onChange={(e) => setReportDescription(e.target.value)}
                        />
                        {errors.reportDescription && <span className="error-text">{errors.reportDescription}</span>}
                    </div>

                    <div className="report-config-grid">
                        <div className="form-group-full">
                            <label className="form-label">Formato de Visualización</label>
                            <select
                                className="form-control-select"
                                value={reportType}
                                onChange={(e) => setReportType(e.target.value)}
                            >
                                <optgroup label="Tabulares">
                                    <option value="listado">Listado de Datos (Tabla)</option>
                                </optgroup>
                                <optgroup label="Gráficos Estadísticos">
                                    <option value="barra">Gráfico de Barras</option>
                                    <option value="linea">Gráfico de Líneas</option>
                                    <option value="area">Gráfico de Área</option>
                                    <option value="radar">Gráfico de Radar</option>
                                </optgroup>
                                <optgroup label="Distribución">
                                    <option value="pie">Gráfico de Pastel (Pie)</option>
                                    <option value="dona">Gráfico de Dona</option>
                                </optgroup>
                            </select>
                        </div>

                        {/* SELECT TABLA BASE CON LIMPIEZA */}
                        <div className="form-group-full">
                            <label className="form-label">Tabla de origen</label>
                            <select
                                className="form-control-select"
                                value={selectedTable}
                                onChange={(e) => {
                                    setSelectedTable(e.target.value);
                                    setQuery({ combinator: 'and', rules: [] });
                                    setSelectedColumns([]);
                                    setFields([]);
                                    setRelatedTables([]);
                                    setAvailableRelations([]);
                                    setGroupByColumns([]);
                                    setLimit("");
                                    setOrderByColumn("");
                                    setOrderDirection("ASC");
                                }}
                            >
                                <option value="">-- Seleccionar tabla --</option>
                                <option value="ventas">Ventas</option>
                                <option value="avance_ventas">Avance de Ventas</option>
                                <option value="clientes">Clientes</option>
                                <option value="productos">Productos</option>
                                <option value="asistencia">Asistencia</option>
                                <option value="leads">Leads</option>
                                <option value="postulantes">Postulantes</option>
                                <option value="tickets">Tickets</option>
                                <option value="usuarios">Usuarios</option>
                                <option value="recibos_emitidos">Recibos</option>
                            </select>
                            {errors.selectedTable && <span className="error-text">{errors.selectedTable}</span>}
                        </div>
                    </div>

                    {selectedTable ? (
                        <div className="builder-section animate-fade">

                            <div className="relations-section">
                                <label className="form-label">1. Seleccionar columnas para el reporte</label>
                                <div className="relations-grid">
                                    {fields.map(f => {
                                        const isSelected = selectedColumns.some(col => col.id === f.name);

                                        return (
                                            <button
                                                key={`${f.relId}-${f.name}`}
                                                type="button"
                                                className={`relation-chip ${isSelected ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setSelectedColumns(selectedColumns.filter(col => col.id !== f.name));
                                                    } else {
                                                        setSelectedColumns([...selectedColumns, { id: f.name, alias: f.label }]);
                                                    }
                                                }}
                                            >
                                                {isSelected ? '✓ ' : '+ '}
                                                <span style={{ fontSize: '0.85rem' }}>{f.label}</span>
                                                <small style={{ display: 'block', fontSize: '0.65rem', opacity: 0.6 }}>
                                                    {f.name.split('.')[0]}
                                                </small>
                                            </button>
                                        );
                                    })}
                                </div>
                                {selectedColumns.length > 0 && (
                                    <div className="selected-alias-section" style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                        <label className="form-label">Configurar nombres en el reporte (Alias)</label>
                                        <div className="alias-container" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {selectedColumns.map((col, index) => (
                                                <div key={col.id} className="alias-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f9f9f9', padding: '8px', borderRadius: '5px' }}>

                                                    <div style={{ flex: 1 }}>
                                                        <small style={{ color: '#666', display: 'block' }}>Origen: {col.id}</small>
                                                    </div>

                                                    <div style={{ flex: 2 }}>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            placeholder="Nombre en el reporte..."
                                                            value={col.alias}
                                                            onChange={(e) => {
                                                                const updated = [...selectedColumns];
                                                                updated[index].alias = e.target.value;
                                                                setSelectedColumns(updated);
                                                            }}
                                                            style={{ width: '100%', padding: '4px 8px', fontSize: '0.9rem' }}
                                                        />
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedColumns(selectedColumns.filter(c => c.id !== col.id))}
                                                        style={{ border: 'none', background: 'transparent', color: '#ff4d4f', cursor: 'pointer' }}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {availableRelations.length > 0 && (
                                <div className="relations-section">
                                    <label className="form-label">2. Vincular información adicional (Relaciones)</label>
                                    <div className="relations-grid">
                                        {availableRelations.map(rel => {
                                            const isActive = relatedTables.some(r => r.id === rel.id);
                                            return (
                                                <button
                                                    key={rel.id}
                                                    type="button"
                                                    className={`relation-chip ${isActive ? 'active' : ''}`}
                                                    onClick={() => toggleRelation(rel, rel.parentAlias || selectedTable)}
                                                >
                                                    {isActive ? '✓ ' : '+ '}
                                                    {rel.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {reportType === "listado" && (
                                <div className="relations-section animate-fade">
                                    <label className="form-label">3. Agrupar por </label>
                                    <div className="relations-grid">
                                        {fields.map(f => (
                                            <button
                                                key={`group-${f.name}`}
                                                type="button"
                                                className={`relation-chip ${groupByColumns.includes(f.name) ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (groupByColumns.includes(f.name)) {
                                                        setGroupByColumns(groupByColumns.filter(col => col !== f.name));
                                                    } else {
                                                        setGroupByColumns([...groupByColumns, f.name]);
                                                    }
                                                }}
                                            >
                                                {groupByColumns.includes(f.name) ? '✓ ' : '+ '}
                                                <span style={{ fontSize: '0.85rem' }}>{f.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="builder-header-info">
                                <h3 className="builder-title">4. Definir Filtros Inteligentes</h3>
                            </div>

                            <div className="custom-query-builder-wrapper ">
                                <QueryBuilder
                                    key={selectedTable}
                                    fields={fields}
                                    query={query}
                                    onQueryChange={q => setQuery(q)}
                                    translations={{
                                        addRule: "+ Añadir Filtro",
                                        addGroup: "+ Añadir Grupo",
                                        removeRule: "Eliminar",
                                        removeGroup: "Eliminar Grupo",
                                        combinators: { and: "Y", or: "O" }
                                    }}
                                />
                            </div>

                            <div className="relations-section animate-fade" style={{ marginTop: 20 }}>
                                <label className="form-label">5. Ordenar resultados por</label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <select
                                        className="form-control-select"
                                        style={{ flex: 2 }}
                                        value={orderByColumn}
                                        onChange={(e) => setOrderByColumn(e.target.value)}
                                    >
                                        <option value="">-- Sin orden específico --</option>
                                        {fields.map(f => (
                                            <option key={`order-${f.name}`} value={f.name}>
                                                {f.label}
                                            </option>
                                        ))}
                                    </select>

                                    <select
                                        className="form-control-select"
                                        style={{ flex: 1 }}
                                        value={orderDirection}
                                        onChange={(e) => setOrderDirection(e.target.value)}
                                        disabled={!orderByColumn}
                                    >
                                        <option value="ASC">Ascendente (A-Z)</option>
                                        <option value="DESC">Descendente (Z-A)</option>
                                    </select>
                                </div>
                            </div>


                            <div className="form-group-full">
                                <label className="form-label">Límite de registros</label>
                                <input
                                    type="number"
                                    className="form-control-select"
                                    placeholder="Ingrese el límite"
                                    value={limit}
                                    min="1"
                                    onChange={(e) => setLimit(e.target.value)}
                                />
                            </div>

                            {reportType !== "listado" && hayAlgunaFechaSeleccionada && (
                                <div className="control-group">
                                    <label>Agrupar fecha por:</label>
                                    <select value={temporalGranularity} onChange={(e) => setTemporalGranularity(e.target.value)}>
                                        <option value="dia">Día</option>
                                        <option value="mes">Mes</option>
                                        <option value="anio">Año</option>
                                    </select>
                                </div>
                            )}


                            <Permiso permiso="view_report_config_sql">
                                <div className="query-preview-container">
                                    <div className="preview-header">
                                        <span className="preview-label">Consulta SQL Generada:</span>
                                    </div>
                                    <div className="preview-code-box">
                                        <code>{buildSQL()}</code>
                                    </div>
                                </div>
                            </Permiso>

                            <div className="builder-footer-actions">
                                <button className="btn btn-secondary" onClick={handleClear}>Limpiar Filtros</button>
                                <button className="btn btn-primary" onClick={guardarConfiguracion}>Guardar Configuración</button>
                            </div>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <p>Selecciona una tabla para comenzar a configurar tu consulta</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegisterReportConfig;